import { adminClient } from './client';
import { SUPPRESS_ADMIN_GLOBAL_ERROR } from '../lib/adminApiErrorPolicy';

/*
 * Local mirrors of the orchestrator's admin DTO shapes.
 * Hand-typed to avoid coupling the SPA build to the orchestrator's
 * generated tsoa-spec output. Fields are kept in lock-step with
 * `orchestrator/src/domain/dtos/admin.dto.ts` — any divergence
 * surfaces as a runtime error in the playground (the canonical
 * shape).
 */

export interface DoctrineSpecDto {
  id: string;
  displayName: string;
  version: number;
  isActive: boolean;
  spec: Record<string, unknown>;
}

export interface DoctrineCreateRequestDto {
  id: string;
  displayName: string;
  version?: number;
  isActive?: boolean;
  spec?: Record<string, unknown>;
}

export interface DoctrineUpdateRequestDto {
  displayName?: string;
  version?: number;
  isActive?: boolean;
  spec?: Record<string, unknown>;
}

export interface StepSpecDto {
  id: string;
  doctrineId: string;
  ordinal: number;
  capability: string;
  required: boolean;
  requiredMilestones: string[];
  emitsMilestone?: string | null;
  inputSchema?: Record<string, unknown> | null;
  outputSchema?: Record<string, unknown> | null;
  timeoutMs: number;
  retryPolicy: Record<string, unknown>;
}

export interface StepCreateRequestDto {
  id: string;
  doctrineId: string;
  ordinal: number;
  capability: string;
  required?: boolean;
  requiredMilestones?: string[];
  emitsMilestone?: string | null;
  inputSchema?: Record<string, unknown> | null;
  outputSchema?: Record<string, unknown> | null;
  timeoutMs?: number;
  retryPolicy?: Record<string, unknown>;
}

export interface StepUpdateRequestDto {
  ordinal?: number;
  capability?: string;
  required?: boolean;
  requiredMilestones?: string[];
  emitsMilestone?: string | null;
  inputSchema?: Record<string, unknown> | null;
  outputSchema?: Record<string, unknown> | null;
  timeoutMs?: number;
  retryPolicy?: Record<string, unknown>;
}

export interface ProviderSpecDto {
  id: string;
  displayName: string;
  adapterKind: 'REST' | 'SOAP' | 'SDK' | 'INTERNAL_SVC' | 'SFTP';
  baseUrl?: string | null;
  config: Record<string, unknown>;
  responseMapping?: Record<string, string> | null;
  responseMappingRev?: string;
  secretRef?: string | null;
  isActive: boolean;
  healthUrl?: string | null;
}

export interface ProviderCreateRequestDto {
  id: string;
  displayName: string;
  adapterKind: 'REST' | 'SOAP' | 'SDK' | 'INTERNAL_SVC' | 'SFTP';
  baseUrl?: string | null;
  config?: Record<string, unknown>;
  responseMapping?: Record<string, string> | null;
  secretRef?: string | null;
  isActive?: boolean;
  healthUrl?: string | null;
}

export interface ProviderUpdateRequestDto {
  displayName?: string;
  adapterKind?: 'REST' | 'SOAP' | 'SDK' | 'INTERNAL_SVC' | 'SFTP';
  baseUrl?: string | null;
  config?: Record<string, unknown>;
  responseMapping?: Record<string, string> | null;
  secretRef?: string | null;
  isActive?: boolean;
  healthUrl?: string | null;
}

export interface RoutingSpecDto {
  id: string;
  doctrineStepId: string;
  tenantId?: string | null;
  productCode?: string | null;
  channel: 'ONLINE' | 'BRANCH' | 'ANY';
  customerMode: 'NEW' | 'RETURNING' | 'BRANCH_LINK' | 'ANY';
  strategy: 'SINGLE' | 'PRIORITY_FALLBACK' | 'RULES';
  spec: Record<string, unknown>;
  isActive: boolean;
}

export interface RoutingCreateRequestDto {
  doctrineStepId: string;
  tenantId?: string | null;
  productCode?: string | null;
  channel: 'ONLINE' | 'BRANCH' | 'ANY';
  customerMode: 'NEW' | 'RETURNING' | 'BRANCH_LINK' | 'ANY';
  strategy: 'SINGLE' | 'PRIORITY_FALLBACK' | 'RULES';
  spec: Record<string, unknown>;
  isActive?: boolean;
}

export interface RoutingUpdateRequestDto {
  doctrineStepId?: string;
  tenantId?: string | null;
  productCode?: string | null;
  channel?: 'ONLINE' | 'BRANCH' | 'ANY';
  customerMode?: 'NEW' | 'RETURNING' | 'BRANCH_LINK' | 'ANY';
  strategy?: 'SINGLE' | 'PRIORITY_FALLBACK' | 'RULES';
  spec?: Record<string, unknown>;
  isActive?: boolean;
}

export interface AuditEntryDto {
  id: string;
  actorUserId: string;
  actorEmail?: string | null;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'INVALIDATE';
  targetTable:
    | 'doctrine_config'
    | 'doctrine_step_config'
    | 'provider_config'
    | 'doctrine_provider_routing';
  targetId: string;
  beforeJson?: Record<string, unknown> | null;
  afterJson?: Record<string, unknown> | null;
  reason?: string | null;
  correlationId?: string | null;
  createdAt: string;
}

export interface AuditListResponseDto {
  total: number;
  limit: number;
  offset: number;
  items: AuditEntryDto[];
}

export interface CacheStatusDto {
  configVersion: string | null;
  ageMs: number | null;
}

export interface PreviewResponseDto {
  output: Record<string, unknown> | null;
  schema?: 'IDENTITY' | 'INCOME' | 'INCOME_PROBE' | 'EMPLOYER' | null;
  valid: boolean;
  errors?: string[];
}

// ── Doctrines ─────────────────────────────────────────────────────────────

export const doctrineApi = {
  async list(): Promise<DoctrineSpecDto[]> {
    const r = await adminClient.get<{ items: DoctrineSpecDto[] }>(
      '/admin/doctrines',
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data.items;
  },
  async get(id: string): Promise<DoctrineSpecDto> {
    const r = await adminClient.get<DoctrineSpecDto>(
      `/admin/doctrines/${id}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async create(body: DoctrineCreateRequestDto): Promise<DoctrineSpecDto> {
    const r = await adminClient.post<DoctrineSpecDto>('/admin/doctrines', body, SUPPRESS_ADMIN_GLOBAL_ERROR);
    return r.data;
  },
  async update(id: string, patch: DoctrineUpdateRequestDto): Promise<DoctrineSpecDto> {
    const r = await adminClient.put<DoctrineSpecDto>(`/admin/doctrines/${id}`, patch, SUPPRESS_ADMIN_GLOBAL_ERROR);
    return r.data;
  },
  async delete(id: string): Promise<void> {
    await adminClient.delete(`/admin/doctrines/${id}`, SUPPRESS_ADMIN_GLOBAL_ERROR);
  },
  async listSteps(id: string): Promise<StepSpecDto[]> {
    const r = await adminClient.get<{ items: StepSpecDto[] }>(
      `/admin/doctrines/${id}/steps`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data.items;
  },
  async createStep(doctrineId: string, body: StepCreateRequestDto): Promise<StepSpecDto> {
    const r = await adminClient.post<StepSpecDto>(
      `/admin/doctrines/${doctrineId}/steps`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async updateStep(
    doctrineId: string,
    stepId: string,
    patch: StepUpdateRequestDto,
  ): Promise<StepSpecDto> {
    const r = await adminClient.put<StepSpecDto>(
      `/admin/doctrines/${doctrineId}/steps/${stepId}`,
      patch,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async deleteStep(doctrineId: string, stepId: string): Promise<void> {
    await adminClient.delete(
      `/admin/doctrines/${doctrineId}/steps/${stepId}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
  },
};

// ── Providers ─────────────────────────────────────────────────────────────

export const providerApi = {
  async list(): Promise<ProviderSpecDto[]> {
    const r = await adminClient.get<{ items: ProviderSpecDto[] }>(
      '/admin/providers',
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data.items;
  },
  async get(id: string): Promise<ProviderSpecDto> {
    const r = await adminClient.get<ProviderSpecDto>(
      `/admin/providers/${id}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async create(body: ProviderCreateRequestDto): Promise<ProviderSpecDto> {
    const r = await adminClient.post<ProviderSpecDto>('/admin/providers', body, SUPPRESS_ADMIN_GLOBAL_ERROR);
    return r.data;
  },
  async update(id: string, patch: ProviderUpdateRequestDto): Promise<ProviderSpecDto> {
    const r = await adminClient.put<ProviderSpecDto>(
      `/admin/providers/${id}`,
      patch,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async delete(id: string): Promise<void> {
    await adminClient.delete(`/admin/providers/${id}`, SUPPRESS_ADMIN_GLOBAL_ERROR);
  },
  async preview(
    id: string,
    body: {
      sampleResponse: unknown;
      responseMapping?: Record<string, string> | null;
      capability?: string | null;
    },
  ): Promise<PreviewResponseDto> {
    const r = await adminClient.post<PreviewResponseDto>(
      `/admin/providers/${id}/preview`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
};

// ── Routing ───────────────────────────────────────────────────────────────

export const routingApi = {
  async list(doctrineStepId?: string): Promise<RoutingSpecDto[]> {
    const r = await adminClient.get<{ items: RoutingSpecDto[] }>('/admin/routing', {
      params: doctrineStepId ? { doctrineStepId } : {},
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return r.data.items;
  },
  async get(id: string): Promise<RoutingSpecDto> {
    const r = await adminClient.get<RoutingSpecDto>(
      `/admin/routing/${id}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async create(body: RoutingCreateRequestDto): Promise<RoutingSpecDto> {
    const r = await adminClient.post<RoutingSpecDto>(
      '/admin/routing',
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async update(id: string, patch: RoutingUpdateRequestDto): Promise<RoutingSpecDto> {
    const r = await adminClient.put<RoutingSpecDto>(
      `/admin/routing/${id}`,
      patch,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async delete(id: string): Promise<void> {
    await adminClient.delete(`/admin/routing/${id}`, SUPPRESS_ADMIN_GLOBAL_ERROR);
  },
};

// ── Applications journey (orchestrator admin) ─────────────────────────────

export interface ApplicationDoctrineRollupDto {
  doctrineId: string;
  status: 'STARTED' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  outcome?: 'PASS' | 'REVIEW' | 'FAIL' | 'CHALLENGE' | null;
  startedAt: string;
  endedAt?: string | null;
}

export interface ApplicationListItemDto {
  applicationId: string;
  ccid?: string | null;
  email?: string | null;
  phone?: string | null;
  attemptedEmail?: string | null;
  attemptedPhone?: string | null;
  status: string;
  productType: string;
  updatedAt: string;
  createdAt: string;
  lastActionAt?: string | null;
  /** Furthest completed journey milestone (plain label). Doctrine health is on doctrineRollups. */
  journeySummaryLabel: string;
  /** The same value as journeySummaryLabel, as the code the JOURNEY filter takes. */
  journeyStage?: string;
  /** Journey area the application stopped in, or null when it is not blocked. */
  blockedArea?: string | null;
  /** failed | pending | withdrawn — null when not blocked. */
  blockedState?: string | null;
  doctrineRollups: ApplicationDoctrineRollupDto[];
  /** Row-level edit marker, so a corrected application never reads as untouched. */
  hasManualEdits?: boolean;
  manualEditCount?: number;
}

export interface ApplicationFilterOptionDto {
  value: string;
  label: string;
}

/**
 * Filter vocabularies served with the list, so the grid never keeps its own copy of the
 * milestone labels it filters on. See the orchestrator DTO for why.
 */
export interface ApplicationListFilterOptionsDto {
  journeyStages: ApplicationFilterOptionDto[];
  blockedAreas: ApplicationFilterOptionDto[];
  blockedStates: ApplicationFilterOptionDto[];
}

export interface ApplicationListResponseDto {
  total: number;
  limit: number;
  offset: number;
  items: ApplicationListItemDto[];
  /** Absent when talking to an orchestrator that predates served options. */
  filterOptions?: ApplicationListFilterOptionsDto;
}

export interface ApplicationJourneyStepDto {
  id: string;
  executionId: string;
  stepId: string;
  providerId?: string | null;
  attempt: number;
  status: 'OK' | 'FAILED' | 'TIMEOUT' | 'CIRCUIT_OPEN' | 'SKIPPED';
  latencyMs?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  startedAt: string;
  endedAt?: string | null;
}

export interface ApplicationJourneyExecutionDto {
  id: string;
  applicationId: string;
  doctrineId: string;
  status: 'STARTED' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  outcome?: 'PASS' | 'REVIEW' | 'FAIL' | 'CHALLENGE' | null;
  correlationId?: string | null;
  startedAt: string;
  endedAt?: string | null;
  metadata?: Record<string, unknown> | null;
  steps: ApplicationJourneyStepDto[];
}

export interface ApplicationJourneyHeaderDto {
  applicationId: string;
  ccid?: string | null;
  email?: string | null;
  phone?: string | null;
  attemptedEmail?: string | null;
  attemptedPhone?: string | null;
  phoneVerifiedAt?: string | null;
  status: string;
  productType: string;
  updatedAt: string;
  createdAt: string;
  lastAction?: string | null;
  lastActionAt?: string | null;
  lastActionOutcome?: string | null;
  lastActionResult?: Record<string, unknown> | null;
  completedMilestones?: string[];
  declineReason?: string | null;
  loanSummary?: {
    requestedPrincipal?: number | null;
    loanTermMonths?: number | null;
    interestRateApr?: number | null;
  } | null;
  attemptedAt?: string | null;
  loanReview?: LoanReviewSummaryDto;
  /** True when a reviewer has manually corrected any field on this application. */
  hasManualEdits?: boolean;
  manualEditCount?: number;
  /** Post-income Decision Engine (svc-de) evaluation for capacity / decline. */
  decisionEngine?: DecisionEngineJourneyDto | null;
  legacyLinkage?: {
    status?: string;
    powerId?: string | null;
    reviewId?: string | null;
    candidateCount?: number | null;
    stampedAt?: string | null;
    source?: string | null;
    matchMethod?: string | null;
    distance?: number | null;
    fundingBlocked?: boolean;
    isMinted?: boolean;
    liveRead?: boolean;
    legacyPii?: {
      firstName: string | null;
      lastName: string | null;
      dateOfBirth: string | null;
    } | null;
    candidates?: Array<{
      powerId: string;
      distance: number | null;
      rank: number;
      piiCorroborated?: boolean | null;
      piiReason?: string | null;
      legacyPii?: {
        firstName: string | null;
        lastName: string | null;
        dateOfBirth: string | null;
      } | null;
      applicantPii?: {
        firstName: string | null;
        lastName: string | null;
        dateOfBirth: string | null;
      } | null;
      givenNameMatch?: boolean | null;
      surnameMatch?: boolean | null;
      dobMatch?: boolean | null;
    }>;
  } | null;
  employmentReviewSignals?: EmploymentReviewSignalsDto;
  phoneFromRelatedApplication?: {
    phone: string;
    phoneVerifiedAt?: string | null;
    applicationId: string;
  } | null;
}

export interface RelatedApplicationSummaryDto {
  applicationId: string;
  status: string;
  productType: string;
  email?: string | null;
  phone?: string | null;
  phoneVerifiedAt?: string | null;
  declineReason?: string | null;
  completedMilestones?: string[];
  createdAt: string;
  updatedAt: string;
}

export type LoanReviewRequestStatusDto = 'PENDING' | 'COMPLETED' | 'FAILED';
export type LoanReviewDecisionDto = 'APPROVE' | 'REVIEW' | 'DECLINE';
/** Accept/Reject action an employee has taken on a MANUAL_REVIEW loan. */
export interface LoanReviewEmployeeDecisionDto {
  decision: 'ACCEPT' | 'REJECT';
  actorUserId: string;
  actorEmail?: string | null;
  reason?: string | null;
  decidedAt: string;
}

export interface LoanReviewSummaryDto {
  requestStatus: LoanReviewRequestStatusDto;
  /** The agent's decision — a proposal while awaiting an employee. */
  decision?: LoanReviewDecisionDto;
  agentDetails?: Record<string, unknown>;
  /** Why the loan is in MANUAL_REVIEW despite a completed agent result. */
  manualReviewHold?: {
    reason: 'EMPLOYMENT_PENDING' | 'AGENT_REVIEW' | 'EMPLOYEE_CONFIRMATION';
    detail?: string | null;
    heldAt: string;
  };
  /** Present once an employee has accepted/rejected the case. */
  employeeDecision?: LoanReviewEmployeeDecisionDto;
}

/** Request body for POST /admin/applications/{id}/decision. */
export interface EmployeeReviewDecisionRequestDto {
  decision: 'ACCEPT' | 'REJECT';
  reason?: string;
  declineReasonCode?: string;
}

export interface EmployeeReviewDecisionResponseDto {
  applicationId: string;
  status: string;
  decision: 'ACCEPT' | 'REJECT';
  decidedByUserId: string;
  decidedAt: string;
  notificationDispatched: boolean;
}

/** Admin Decision tab — svc-de evaluation snapshot from journey header. */
export interface PdlAffordabilityTraceStepDto {
  code: string;
  detail: string;
  value?: number | string | boolean | null;
}

export interface PdlAffordabilityJourneyDto {
  monthlyIncome?: number | null;
  existingMonthlyDebtServiceCents?: number | null;
  availableForNewPaymentCents?: number | null;
  debtServiceRatio?: number | null;
  paydayStackingCount?: number | null;
  debtPaymentNsfCount90d?: number | null;
  loanVelocity90d?: number | null;
  existingLoanCount?: number | null;
  hasInternalC4YExposure?: boolean | null;
  elodAvailable?: boolean | null;
  detectionConfidence?: string | null;
  decisionSummary?: string | null;
  capacityReason?: string | null;
  reviewReasons?: string[];
  policyVersion?: string | null;
  affordabilityTrace?: PdlAffordabilityTraceStepDto[];
  remarks?: PdlAffordabilityRemarkDto[];
}

export interface PdlAffordabilityRemarkDto {
  code: string;
  category: string;
  message: string;
  value?: number | string | boolean | null;
}

export interface DecisionEngineJourneyDto {
  evaluationId?: string;
  decision?: string;
  maxEligibleAmount?: number;
  declinedReason?: string | null;
  declinedExplanation?: string | null;
  decisionSummary?: string | null;
  capacityReason?: string | null;
  interestRate?: number | null;
  approvedAmount?: number | null;
  documentNumber?: string | null;
  identifier?: string | null;
  provider?: string | null;
  status?: string;
  creditScore?: number | null;
  requestedAmount?: number | null;
  productCode?: string | null;
  dtiRate?: number | null;
  offerMinAmount?: number | null;
  offerMaxAmount?: number | null;
  maxTerm?: number | null;
  opLoanStatus?: string | null;
  powerId?: string | null;
  inquiryId?: string | null;
  caStatus?: string | null;
  employerName?: string | null;
  occupation?: string | null;
  monthlyIncome?: number | null;
  applicantFirstName?: string | null;
  applicantLastName?: string | null;
  tuHitStatus?: string | null;
  tuOnFileDate?: string | null;
  tuBureauScore?: number | null;
  inquiryCount6m?: number | null;
  pastAddress?: {
    street?: string | null;
    city?: string | null;
    province?: string | null;
    postal?: string | null;
    dateReported?: string | null;
  } | null;
  currentAddress?: {
    street?: string | null;
    city?: string | null;
    province?: string | null;
    postal?: string | null;
    dateReported?: string | null;
  } | null;
  activeMortgageIndicator?: boolean | null;
  existingLoans?: Array<{
    memberName?: string | null;
    accountType?: string | null;
    balance?: number | null;
    payment?: number | null;
    status?: string | null;
    dateOpened?: string | null;
  }>;
  previousLoanCount?: number | null;
  chanceOfDefault?: number | null;
  adjCalibDecision?: string | null;
  finCalibDecision?: string | null;
  lendingAmount?: number | null;
  dtiBasedLendingAmount?: number | null;
  productTableMaxAmount?: number | null;
  layers?: {
    hasRaw: boolean;
    hasTransformed: boolean;
    hasResult: boolean;
  };
  /**
   * Fused (bank/ELOD + TransUnion) loan-capacity decision — the itemised,
   * provenance-tagged obligation ledger + the derived offer. Present only when
   * fused capacity ran (INCOME_FUSED_CAPACITY_ENABLED). Mirrors the orchestrator
   * loan_capacity_offer.fused / svc-income-verification LoanCapacityDecision.
   */
  fusedCapacity?: FusedCapacityDto | null;
  /** Bank + ELOD affordability for payday (PDL_AFFORDABILITY). */
  pdlAffordability?: PdlAffordabilityJourneyDto | null;
  /**
   * Itemised ELOD obligations behind the PDL debt summary (orchestrator
   * metadata.pdl_existing_loans). Null for applications evaluated before it was
   * stamped. Unrelated to `existingLoans` above, which is IL bureau trades.
   */
  pdlExistingLoans?: PdlExistingLoansDto | null;
}

/** One ELOD-detected obligation on a payday application (mirrors income-verification DebtObligation). */
export interface PdlDebtObligationDto {
  lender: string;
  classification: string;
  isInternalC4Y: boolean;
  paymentCount: number;
  medianPaymentAmount: number;
  medianGapDays: number;
  /** WEEKLY · BIWEEKLY · SEMI_MONTHLY · MONTHLY · IRREGULAR */
  frequency: string;
  /** Net monthly debt service (cents) — rows sum to existingMonthlyDebtServiceCents. */
  monthlyAmountCents: number;
  grossMonthlyAmountCents: number;
  isRevolving: boolean;
  isPaydayStyle: boolean;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  firstPaymentDate: string | null;
  lastPaymentDate: string | null;
}

export interface PdlExistingLoansDto {
  obligations: PdlDebtObligationDto[];
  capturedAt: string | null;
}

/** One line of the fused obligation ledger (mirrors income-verification LedgerLine). */
export interface FusedCapacityLedgerLineDto {
  lender: string;
  /** BOTH = bank + TU agree · BANK_ONLY = we found, TU missed · TU_ONLY = TU found, we missed. */
  source: 'BOTH' | 'BANK_ONLY' | 'TU_ONLY';
  monthlyAmountCents: number;
  basis: 'TU_CONTRACTUAL' | 'BANK_OBSERVED' | 'DISCREPANCY_CONSERVATIVE' | 'NONE';
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  category: string;
  counted: boolean;
  bankObservedCents?: number | null;
  tuContractualCents?: number | null;
  tuTradelineCount?: number;
  flags?: string[];
}

/** Fused loan-capacity decision (mirrors income-verification LoanCapacityDecision). */
export interface FusedCapacityDto {
  ledger: FusedCapacityLedgerLineDto[];
  fusedMonthlyDebtServiceCents: number;
  advisoryMonthlyDebtServiceCents?: number;
  availableForNewPaymentCents: number;
  maxEligibleAmountCents: number;
  deBureauMaxCents?: number | null;
  requestedCents?: number | null;
  finalOfferCents: number;
  bindingConstraint: string;
  decision: 'APPROVE' | 'REVIEW';
  reviewReasons: string[];
}

export interface EmploymentReviewSignalEntryDto {
  capturedAt: string;
  verificationId?: string;
  attemptNumber?: number;
  bundle: Record<string, unknown>;
}

export interface EmploymentReviewSignalsDto {
  latest?: EmploymentReviewSignalEntryDto;
  history?: EmploymentReviewSignalEntryDto[];
}

export type ApplicationTimelineEventKind =
  | 'APPLICATION_CREATED'
  | 'CONTACT_ATTEMPT'
  | 'PHONE_VERIFIED'
  | 'ORCHESTRATOR_ACTION'
  | 'STATUS_CHANGE'
  | 'DOCTRINE_RUN'
  | 'DOCTRINE_STEP'
  | 'IDENTITY_SESSION'
  | 'INCOME_RECORD'
  | 'EMPLOYER_RECORD';

export type ApplicationTimelineTone = 'SUCCESS' | 'FAILURE' | 'INFO' | 'WARNING';

export interface ApplicationTimelineRefDto {
  kind: string;
  id: string;
}

export interface ApplicationTimelineEventDto {
  id: string;
  occurredAt: string;
  kind: ApplicationTimelineEventKind;
  title: string;
  subtitle?: string | null;
  tone: ApplicationTimelineTone;
  correlationId?: string | null;
  ref?: ApplicationTimelineRefDto | null;
  detail?: Record<string, unknown> | null;
}

export interface ApplicationVerificationSnapshotErrorDto {
  scope: 'identity' | 'income' | 'employer';
  message: string;
}

export interface ApplicationVerificationSnapshotDto {
  identitySessions: unknown | null;
  incomeRecords: unknown | null;
  employerRecords: unknown | null;
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[];
}

export interface ApplicationFieldOverrideDto {
  fieldKey: string;
  targetService: string;
  targetTable: string;
  targetColumn: string;
  targetRowId?: string | null;
  value?: unknown;
  valueType: string;
  reasonCode: string;
  reasonLabel: string;
  reasonDetail: string;
  setByUserId: string;
  setByEmail?: string | null;
  setAt: string;
}

export interface ApplicationJourneyResponseDto {
  application: ApplicationJourneyHeaderDto;
  /**
   * Live reviewer corrections. Served as data rather than merged into the raw
   * downstream payloads so the UI can show the correction WITH the evidence it
   * shadows. Absent on an orchestrator that predates admin editing.
   */
  fieldOverrides?: ApplicationFieldOverrideDto[];
  executions: ApplicationJourneyExecutionDto[];
  verification: ApplicationVerificationSnapshotDto;
  /** Present when orchestrator supports merged activity timeline. */
  timeline?: ApplicationTimelineEventDto[];
  timelineTruncated?: boolean;
  timelineTotalCount?: number;
  relatedApplications?: RelatedApplicationSummaryDto[];
}

export interface DeviceIdentificationAssessmentDto {
  id: string;
  eventId: string;
  visitorId: string | null;
  checkpoint: string;
  userId: string | null;
  decision: string;
  policyReasons: string[];
  signalSummary: Record<string, unknown> | null;
  rawEventResponse: Record<string, unknown> | null;
  detectedSignals: Array<{ signal: string; value: unknown }> | null;
  createdAt: string;
}

export interface DeviceRiskVerdictDto {
  level: 'clear' | 'review' | 'blocked' | 'observe';
  headline: string;
  reasons: string[];
}

export interface VisitorProfileDto {
  visitorId: string;
  ipAddress: string | null;
  ipCountry: string | null;
  browserName: string | null;
  os: string | null;
  lastSeenAt: string;
}

export interface VisitorApplicationHistoryDto {
  applicationId: string;
  status: string;
  productType: string;
  deviceRiskReview: boolean;
  declineReason: string | null;
  assessmentCount: number;
  worstDecision: string;
  topDetectedSignals: string[];
  firstSeenAt: string;
  lastSeenAt: string;
  isCurrent: boolean;
}

export interface VisitorTimelineEventDto {
  id: string;
  eventId: string;
  applicationId: string | null;
  isCurrentApplication: boolean;
  visitorId: string | null;
  checkpoint: string;
  userId: string | null;
  decision: string;
  policyReasons: string[];
  signalSummary: Record<string, unknown> | null;
  rawEventResponse: Record<string, unknown> | null;
  detectedSignals: Array<{ signal: string; value: unknown }> | null;
  createdAt: string;
}

export interface OtpAttemptChannelSummaryDto {
  channel: string;
  sessions: number;
  attemptsUsedTotal: number;
  maxAttemptsTotal: number;
  sendRequested: number;
  sendSuccess: number;
  sendFailed: number;
  verifyRequested: number;
  verifySuccess: number;
  verifyFailed: number;
  lockedSessions: number;
  expiredSessions: number;
}

export interface OtpAttemptEventDto {
  id: string;
  sessionId: string | null;
  sessionRef: string | null;
  applicationId: string | null;
  channel: string | null;
  eventType: string;
  providerStatus: string | null;
  errorCode: string | null;
  attemptsUsed: number | null;
  maxAttempts: number | null;
  sessionStatus: string | null;
  createdAt: string;
}

export interface OtpAttemptSessionDto {
  id: string;
  sessionRef: string;
  channel: string;
  purpose: string;
  destination: string | null;
  status: string;
  attemptsUsed: number;
  maxAttempts: number;
  verifiedAt: string | null;
  createdAt: string;
}

export interface OtpAttemptSummaryDto {
  applicationId: string;
  sessionsTotal: number;
  eventsTotal: number;
  latestEventAt: string | null;
  channels: OtpAttemptChannelSummaryDto[];
  sessions: OtpAttemptSessionDto[];
  recentEvents: OtpAttemptEventDto[];
}

export interface ApplicationOnboardingContactComparisonDto {
  attemptedEmailMatchesOnFile: boolean | null;
  attemptedPhoneMatchesOnFile: boolean | null;
  otpEmailMatchesOnFile: boolean | null;
  otpEmailMatchesAttempted: boolean | null;
  otpPhoneMatchesOnFile: boolean | null;
  otpPhoneMatchesAttempted: boolean | null;
  otpEmailDestinations: string[];
  otpPhoneDestinations: string[];
}

export type ApplicationContactAccessOutcome = 'success' | 'failure' | 'info';

export interface ApplicationContactAccessEventDto {
  id: string;
  occurredAt: string;
  action: string;
  contactType: 'email' | 'phone' | 'account';
  contactValue: string | null;
  outcome: ApplicationContactAccessOutcome;
  needsReview: boolean;
  reviewReason: string | null;
  sourceApplicationId?: string | null;
  sourceApplicationStatus?: string | null;
  isPriorApplication?: boolean;
}

export interface VisitorContactApplicationRefDto {
  applicationId: string;
  status: string;
  declineReason: string | null;
  isCurrent: boolean;
  sources: string[];
}

export interface VisitorContactUsageDto {
  contactType: 'email' | 'phone';
  value: string;
  usedOnCurrentApplication: boolean;
  needsReview: boolean;
  reviewReason: string | null;
  seenOnApplications: VisitorContactApplicationRefDto[];
}

export interface VisitorContactInventoryDto {
  emails: VisitorContactUsageDto[];
  phones: VisitorContactUsageDto[];
}

export interface ApplicationOnboardingSignalsDto {
  applicationId: string;
  emailOnFile: string | null;
  phoneOnFile: string | null;
  attemptedEmail: string | null;
  attemptedPhone: string | null;
  phoneVerifiedAt: string | null;
  applicationCreatedAt: string;
  contactComparison: ApplicationOnboardingContactComparisonDto;
  contactEvents: ApplicationContactAccessEventDto[];
  visitorContactInventory: VisitorContactInventoryDto;
  priorApplicationCount: number;
  authCheckpoints: VisitorTimelineEventDto[];
  otpAttempts: OtpAttemptSummaryDto | null;
}

export interface ApplicationDeviceRiskResponseDto {
  applicationId: string;
  deviceRiskReview: boolean;
  lastVisitorId: string | null;
  riskVerdict: DeviceRiskVerdictDto;
  visitorProfile: VisitorProfileDto | null;
  visitorApplicationHistory: VisitorApplicationHistoryDto[];
  assessments: DeviceIdentificationAssessmentDto[];
  visitorTimeline: VisitorTimelineEventDto[];
  otpAttempts: OtpAttemptSummaryDto | null;
  onboarding: ApplicationOnboardingSignalsDto;
}

// ── Application editing, notes and change history ─────────────────────────
// Mirrors of the orchestrator DTOs, hand-typed like the rest of this file.

/** Machine code for why a field is locked. The wording lives in this app. */
export type ApplicationFieldLockReason = 'PERMISSION' | 'STATUS' | 'ASSIGNMENT' | 'LOCKED';

export interface ApplicationEditableFieldDto {
  key: string;
  label: string;
  group: string;
  /** Where the value canonically lives. Shown so operators know what a change touches. */
  targetService: string;
  targetTable: string;
  targetColumn: string;
  valueType: string;
  /** DIRECT = the real column is written. OVERLAY = a correction stored against this application. */
  writeMode: string;
  currentValue?: unknown;
  editable: boolean;
  notEditableReason?: ApplicationFieldLockReason | null;
  /**
   * Must be echoed back on save. The server rejects the change-set if this field
   * has been changed by someone else since the descriptor was fetched.
   */
  lastChangeId?: string | null;
  lastChangedAt?: string | null;
  lastChangedBy?: string | null;
  /** True when the displayed value is a reviewer correction, not the source value. */
  overridden?: boolean;
  /** What the owning service still holds. Rendered beneath a correction, never replaced by it. */
  sourceValue?: unknown;
  /** False when the owning service could not be reached — the value is unknown, not empty. */
  sourceResolved?: boolean;
  sourceUnresolvedReason?: string | null;
}

export interface ApplicationChangeReasonOptionDto {
  code: string;
  label: string;
}

export interface ApplicationEditableFieldsResponseDto {
  applicationId: string;
  status: string;
  /** Owner of the case, when assignment is in force. Null until that ships. */
  assignedToUserId?: string | null;
  assignedToEmail?: string | null;
  fields: ApplicationEditableFieldDto[];
  reasonCodes: ApplicationChangeReasonOptionDto[];
  manualEditCount: number;
}

export interface ApplicationFieldChangeRequestDto {
  fieldKey: string;
  value?: unknown;
  lastChangeId?: string | null;
}

export interface ApplicationFieldEditRequestDto {
  expectedStatus: string;
  reasonCode: string;
  reasonDetail: string;
  changes: ApplicationFieldChangeRequestDto[];
}

export interface ApplicationFieldEditResponseDto {
  applicationId: string;
  status: string;
  changed: string[];
  /** Proposed values that already matched — nothing was written for these. */
  unchanged: string[];
  /** Derived fields the server changed as a consequence, each separately logged. */
  sideEffects: string[];
}

export interface ApplicationOverrideClearRequestDto {
  expectedStatus: string;
  reasonCode: string;
  reasonDetail: string;
}

export interface ApplicationOverrideClearResponseDto {
  applicationId: string;
  fieldKey: string;
  cleared: boolean;
}

export interface ApplicationChangeEntryDto {
  id: string;
  changeSetId: string;
  applicationId: string;
  actorUserId: string;
  actorEmail?: string | null;
  actorRole?: string | null;
  action: 'UPDATE' | 'OVERRIDE_SET' | 'OVERRIDE_CLEAR' | 'DECISION' | 'REOPEN';
  targetService: string;
  targetTable: string;
  targetColumn: string;
  targetRowId?: string | null;
  fieldKey: string;
  valueType: string;
  beforeValue?: string | null;
  afterValue?: string | null;
  beforeJson?: Record<string, unknown> | null;
  afterJson?: Record<string, unknown> | null;
  writeMode: 'DIRECT' | 'OVERLAY';
  reasonCode: string;
  reasonLabel: string;
  reasonDetail: string;
  source: string;
  correlationId?: string | null;
  createdAt: string;
}

export interface ApplicationChangeListResponseDto {
  total: number;
  limit: number;
  offset: number;
  items: ApplicationChangeEntryDto[];
}

export interface ApplicationNoteDto {
  id: string;
  applicationId: string;
  body: string;
  category?: string | null;
  pinned: boolean;
  authorUserId: string;
  authorEmail?: string | null;
  authorRole?: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string | null;
  /** Server-computed: only the author may edit. Never inferred client-side. */
  editableByCaller: boolean;
  /** Author OR super admin. Distinct from editable — the client cannot know the role. */
  archivableByCaller: boolean;
}

export interface ApplicationNoteListResponseDto {
  total: number;
  items: ApplicationNoteDto[];
  categories: { code: string; label: string }[];
}

export const applicationsApi = {
  async list(params: {
    limit?: number;
    offset?: number;
    applicationIdContains?: string;
    ccidContains?: string;
    emailContains?: string;
    phoneContains?: string;
    status?: string;
    productType?: string;
    createdFrom?: string;
    createdTo?: string;
    updatedFrom?: string;
    updatedTo?: string;
    /** Journey area the application is blocked in — filtered in-memory like loan-ops search. */
    blockedArea?: string;
    /** failed | pending | withdrawn. */
    blockedState?: string;
    /** Milestone code or not_started — the JOURNEY column's own value. */
    journeyStage?: string;
    /** Only applications a reviewer has manually corrected. Filtered server-side. */
    editedOnly?: boolean;
  }): Promise<ApplicationListResponseDto> {
    const r = await adminClient.get<ApplicationListResponseDto>('/admin/applications', {
      params,
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return r.data;
  },
  async journey(applicationId: string): Promise<ApplicationJourneyResponseDto> {
    const r = await adminClient.get<ApplicationJourneyResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/journey`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async deviceRisk(applicationId: string): Promise<ApplicationDeviceRiskResponseDto> {
    const r = await adminClient.get<ApplicationDeviceRiskResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/device-risk`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  /** What this caller may edit on this application, with current values. */
  async editableFields(applicationId: string): Promise<ApplicationEditableFieldsResponseDto> {
    const r = await adminClient.get<ApplicationEditableFieldsResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/editable-fields`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  /**
   * Apply corrections. All-or-nothing.
   *
   * 409 means either the application moved status, or another reviewer changed one
   * of these fields first — both are shown inline rather than as a toast, because
   * the reviewer needs to see the conflicting value before deciding.
   */
  async editFields(
    applicationId: string,
    body: ApplicationFieldEditRequestDto,
  ): Promise<ApplicationFieldEditResponseDto> {
    const r = await adminClient.patch<ApplicationFieldEditResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/fields`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  /** Withdraw a correction, restoring what the owning service holds. */
  async clearFieldOverride(
    applicationId: string,
    fieldKey: string,
    body: ApplicationOverrideClearRequestDto,
  ): Promise<ApplicationOverrideClearResponseDto> {
    const r = await adminClient.post<ApplicationOverrideClearResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/fields/${encodeURIComponent(fieldKey)}/clear`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  /** Recorded admin changes, newest first. */
  async changes(
    applicationId: string,
    params?: { limit?: number; offset?: number; fieldKey?: string },
  ): Promise<ApplicationChangeListResponseDto> {
    const r = await adminClient.get<ApplicationChangeListResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/changes`,
      { params, ...SUPPRESS_ADMIN_GLOBAL_ERROR },
    );
    return r.data;
  },
  async notes(
    applicationId: string,
    params?: { includeArchived?: boolean },
  ): Promise<ApplicationNoteListResponseDto> {
    const r = await adminClient.get<ApplicationNoteListResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/notes`,
      { params, ...SUPPRESS_ADMIN_GLOBAL_ERROR },
    );
    return r.data;
  },
  async createNote(
    applicationId: string,
    body: { body: string; category?: string | null; pinned?: boolean },
  ): Promise<ApplicationNoteDto> {
    const r = await adminClient.post<ApplicationNoteDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/notes`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async updateNote(
    applicationId: string,
    noteId: string,
    body: { body?: string; category?: string | null; pinned?: boolean },
  ): Promise<ApplicationNoteDto> {
    const r = await adminClient.patch<ApplicationNoteDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/notes/${encodeURIComponent(noteId)}`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async archiveNote(applicationId: string, noteId: string): Promise<ApplicationNoteDto> {
    const r = await adminClient.delete<ApplicationNoteDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/notes/${encodeURIComponent(noteId)}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  /** Accept/reject a loan that is awaiting manual review. */
  async decide(
    applicationId: string,
    body: EmployeeReviewDecisionRequestDto,
  ): Promise<EmployeeReviewDecisionResponseDto> {
    const r = await adminClient.post<EmployeeReviewDecisionResponseDto>(
      `/admin/applications/${encodeURIComponent(applicationId)}/decision`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async getLegacyLinkageMapping(
    applicationId: string,
  ): Promise<{
    status: string;
    powerId?: string | null;
    reviewId?: string | null;
    candidateCount?: number | null;
    candidates?: unknown[];
  }> {
    const r = await adminClient.get<{
      success: boolean;
      data: {
        status: string;
        powerId?: string | null;
        reviewId?: string | null;
        candidateCount?: number | null;
        candidates?: unknown[];
      };
    }>(`/admin/legacy-linkage/applications/${encodeURIComponent(applicationId)}/mapping`, SUPPRESS_ADMIN_GLOBAL_ERROR);
    return r.data.data;
  },
  async selectLegacyLinkageReview(
    reviewId: string,
    body: { powerId: string; resolvedBy?: string },
  ): Promise<{ success: boolean; data: unknown }> {
    const r = await adminClient.post<{ success: boolean; data: unknown }>(
      `/admin/legacy-linkage/reviews/${encodeURIComponent(reviewId)}/select`,
      body,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async rejectAllLegacyLinkageReview(
    reviewId: string,
    body?: { resolvedBy?: string },
  ): Promise<{ success: boolean; data: unknown }> {
    const r = await adminClient.post<{ success: boolean; data: unknown }>(
      `/admin/legacy-linkage/reviews/${encodeURIComponent(reviewId)}/reject-all`,
      body ?? {},
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  /** Nudge IV outbox to (re)attempt resolve for this application's ccid. */
  async ensureLegacyLinkage(
    applicationId: string,
  ): Promise<{ success: boolean; data: unknown }> {
    const r = await adminClient.post<{ success: boolean; data: unknown }>(
      `/admin/legacy-linkage/applications/${encodeURIComponent(applicationId)}/ensure`,
      {},
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
};

export const MANUAL_REVIEW_QUEUE_QUERY_KEY = ['manualReviewQueue'] as const;
export const MANUAL_REVIEW_REVIEWERS_QUERY_KEY = ['manualReviewReviewers'] as const;

export type ManualReviewQueueItemDto = {
  applicationId: string;
  state: string;
  queuedAt: string;
  assigneeEmail: string | null;
  assigneeDisplayName: string | null;
  assignedAt: string | null;
  assignmentCount: number;
  queuePosition: number | null;
  assignedToCaller: boolean;
};

export type ManualReviewReviewerDto = {
  email: string;
  displayName: string;
  onShift: boolean;
  holdingApplicationId: string | null;
};

export const manualReviewApi = {
  async queue(limit?: number): Promise<{ items: ManualReviewQueueItemDto[] }> {
    const r = await adminClient.get<{ items: ManualReviewQueueItemDto[] }>(
      '/admin/manual-review/queue',
      { params: limit != null ? { limit } : undefined, ...SUPPRESS_ADMIN_GLOBAL_ERROR },
    );
    return r.data;
  },
  async assignment(applicationId: string): Promise<{ assignment: ManualReviewQueueItemDto | null }> {
    const r = await adminClient.get<{ assignment: ManualReviewQueueItemDto | null }>(
      `/admin/manual-review/applications/${encodeURIComponent(applicationId)}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async reviewers(): Promise<{ reviewers: ManualReviewReviewerDto[]; rosterAvailable: boolean }> {
    const r = await adminClient.get<{ reviewers: ManualReviewReviewerDto[]; rosterAvailable: boolean }>(
      '/admin/manual-review/reviewers',
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async reassign(
    applicationId: string,
    body?: { toReviewerEmail?: string },
  ): Promise<{ applicationId: string; state: string; assigneeEmail: string | null }> {
    const r = await adminClient.post<{
      applicationId: string;
      state: string;
      assigneeEmail: string | null;
    }>(
      `/admin/manual-review/applications/${encodeURIComponent(applicationId)}/reassign`,
      body ?? {},
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
};

export type AdminAppNotificationItem = {
  id: string;
  title: string;
  body: string;
  deepLink?: string | null;
  readAt?: string | null;
  createdAt: string;
  applicationId?: string | null;
};

export const ADMIN_NOTIFICATIONS_QUERY_KEY = ['adminNotifications'] as const;
export const ADMIN_NOTIFICATIONS_UNREAD_QUERY_KEY = ['adminNotificationsUnread'] as const;

export const adminNotificationsApi = {
  async list(params?: { unreadOnly?: boolean; limit?: number }): Promise<AdminAppNotificationItem[]> {
    const r = await adminClient.get<AdminAppNotificationItem[]>('/admin/notifications/app', {
      params,
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return Array.isArray(r.data) ? r.data : [];
  },
  async unreadCount(): Promise<number> {
    const r = await adminClient.get<{ count: number }>('/admin/notifications/app/unread-count', {
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return typeof r.data?.count === 'number' ? r.data.count : 0;
  },
  async markRead(notificationId: string): Promise<void> {
    await adminClient.post(
      `/admin/notifications/app/${encodeURIComponent(notificationId)}/mark-read`,
      {},
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
  },
  async markAllRead(): Promise<void> {
    await adminClient.post('/admin/notifications/app/read-all', {}, SUPPRESS_ADMIN_GLOBAL_ERROR);
  },
};

/** Allowlisted GET proxies — responses are service-specific JSON. */
export const adminDashboardBffApi = {
  async incomeSummary(): Promise<unknown> {
    const r = await adminClient.get<unknown>(
      '/admin/bff/dashboard/income-verification/summary',
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async incomeRecords(params?: Record<string, string | number | boolean>): Promise<unknown> {
    const r = await adminClient.get<unknown>('/admin/bff/dashboard/income-verification/records', {
      params,
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return r.data;
  },
  async incomeRecordDetail(id: string): Promise<unknown> {
    const r = await adminClient.get<unknown>(
      `/admin/bff/dashboard/income-verification/records/${encodeURIComponent(id)}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async incomeSessionLifecycle(sessionId: string): Promise<unknown> {
    const r = await adminClient.get<unknown>(
      `/admin/bff/dashboard/income-verification/sessions/${encodeURIComponent(sessionId)}/lifecycle`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async identitySummary(): Promise<unknown> {
    const r = await adminClient.get<unknown>(
      '/admin/bff/dashboard/identity-verification/summary',
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async identitySessions(params?: Record<string, string | number | boolean>): Promise<unknown> {
    const r = await adminClient.get<unknown>('/admin/bff/dashboard/identity-verification/sessions', {
      params,
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return r.data;
  },
  async identitySessionDetail(ivVerificationId: string): Promise<unknown> {
    const r = await adminClient.get<unknown>(
      `/admin/bff/dashboard/identity-verification/sessions/${encodeURIComponent(ivVerificationId)}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async employerSummary(): Promise<unknown> {
    const r = await adminClient.get<unknown>(
      '/admin/bff/dashboard/employer-verification/summary',
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async employerRecords(params?: Record<string, string | number | boolean>): Promise<unknown> {
    const r = await adminClient.get<unknown>('/admin/bff/dashboard/employer-verification/records', {
      params,
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return r.data;
  },
  async employerRecordDetail(id: string): Promise<unknown> {
    const r = await adminClient.get<unknown>(
      `/admin/bff/dashboard/employer-verification/records/${encodeURIComponent(id)}`,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
};

// ── Audit ─────────────────────────────────────────────────────────────────

export const auditApi = {
  async list(params: {
    limit?: number;
    offset?: number;
    actorUserId?: string;
    action?: 'CREATE' | 'UPDATE' | 'DELETE' | 'INVALIDATE';
    targetTable?:
      | 'doctrine_config'
      | 'doctrine_step_config'
      | 'provider_config'
      | 'doctrine_provider_routing';
    targetId?: string;
    createdAfter?: string;
    createdBefore?: string;
  }): Promise<AuditListResponseDto> {
    const r = await adminClient.get<AuditListResponseDto>('/admin/audit', {
      params,
      ...SUPPRESS_ADMIN_GLOBAL_ERROR,
    });
    return r.data;
  },
};

// ── Cache ─────────────────────────────────────────────────────────────────

export const cacheApi = {
  async status(): Promise<CacheStatusDto> {
    const r = await adminClient.get<CacheStatusDto>(
      '/admin/cache/status',
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
  async invalidate(): Promise<{ invalidated: true; invalidatedAt: string }> {
    const r = await adminClient.post<{ invalidated: true; invalidatedAt: string }>(
      '/admin/cache/invalidate',
      {},
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    return r.data;
  },
};

// ── Auth ──────────────────────────────────────────────────────────────────

